/**
 * ErrorReportModal
 * 
 * Centralized error modal with built-in bug reporting.
 * 
 * Two modes:
 *   - 'system': Shows error message + optional "What were you trying to do?" text box
 *               + single "Send Report" button. Auto-captures context and submits to
 *               /api/error-reports -> chatbot_issues table -> Admin Dashboard Bugs tab.
 *   - 'validation': Shows error message + single "OK" dismiss button. No report sent.
 * 
 * Users can always dismiss via the X close button without sending a report.
 */

"use client";

import React, { useState, useCallback } from 'react';
import { Modal } from '@/components/ui/modal';
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';
import Button from '@/components/ui/button/Button';
import type { ErrorState } from '@/hooks/ui/useErrorReport';

interface ErrorReportModalProps {
  errorState: ErrorState;
  onClose: () => void;
  onSubmitReport: (userComment?: string) => Promise<void>;
  isSubmitting: boolean;
}

const ErrorReportModal: React.FC<ErrorReportModalProps> = ({
  errorState,
  onClose,
  onSubmitReport,
  isSubmitting,
}) => {
  const [userComment, setUserComment] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleClose = useCallback(() => {
    setUserComment('');
    setSubmitted(false);
    onClose();
  }, [onClose]);

  const handleSendReport = useCallback(async () => {
    await onSubmitReport(userComment);
    setSubmitted(true);
    setTimeout(() => {
      setUserComment('');
      setSubmitted(false);
      onClose();
    }, 1500);
  }, [onSubmitReport, userComment, onClose]);

  const handleOk = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const isSystem = errorState.type === 'system';

  if (submitted) {
    return (
      <Modal isOpen={errorState.isOpen} onClose={handleClose} className="max-w-md" showCloseButton={false}>
        <Modal.Content>
          <div className="flex flex-col items-center py-8 gap-3">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30">
              <GoogleMaterialIcon name="check_circle" className="text-green-600 dark:text-green-400" size="md" />
            </div>
            <p className="text-base font-medium text-gray-900 dark:text-white">
              Thanks, we&apos;ve been notified!
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Our team will look into this.
            </p>
          </div>
        </Modal.Content>
      </Modal>
    );
  }

  return (
    <Modal isOpen={errorState.isOpen} onClose={handleClose} className="max-w-md">
      <Modal.Header>
        <div className="flex items-center gap-3">
          <div className={`flex items-center justify-center w-10 h-10 flex-shrink-0 rounded-full ${
            isSystem
              ? 'bg-red-100 dark:bg-red-900/30'
              : 'bg-amber-100 dark:bg-amber-900/30'
          }`}>
            <GoogleMaterialIcon
              name={isSystem ? 'error' : 'warning'}
              className={isSystem
                ? 'text-red-600 dark:text-red-400'
                : 'text-amber-600 dark:text-amber-400'
              }
            />
          </div>
          <span className="text-lg font-semibold text-gray-900 dark:text-white">
            {errorState.title}
          </span>
        </div>
      </Modal.Header>

      <Modal.Content>
        <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
          {errorState.message}
        </p>

        {isSystem && (
          <div className="mt-4">
            <label
              htmlFor="error-report-comment"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
            >
              What were you trying to do? <span className="font-normal text-gray-400 dark:text-gray-500">(optional)</span>
            </label>
            <textarea
              id="error-report-comment"
              rows={3}
              value={userComment}
              onChange={(e) => setUserComment(e.target.value)}
              placeholder="e.g. I was trying to invite a new coach..."
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none resize-none"
            />
          </div>
        )}
      </Modal.Content>

      <Modal.Footer variant="actions">
        {isSystem ? (
          <Button
            variant="primary"
            onClick={handleSendReport}
            isLoading={isSubmitting}
            disabled={isSubmitting}
          >
            Send Report
          </Button>
        ) : (
          <Button variant="outline" onClick={handleOk}>
            OK
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ErrorReportModal;
